import Help from './Help';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import BuildIcon from '@material-ui/icons/Build';
import DoneIcon from '@material-ui/icons/Done';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import GS from '../grafsync/grafsync';
import BasicReadField from './BasicReadField';
import CancelButton from './CancelButton';
import Errored from './Errored';
import PrimaryButton from './PrimaryButton';
import { globalStyles as styles } from './style/gstheme';
import TitledPanel from './TitledPanel';
import TitledTextField from './TitledTextField';
import { UserConsumer } from './providers/UserProvider';
import { MAX_LONGIRI_LENGTH, RTL_REGEX } from './utils/constants';
import { detailedCheckIRI, debounce, makeCancelable, sanitiseText } from './utils/utils';

// Do not allow RDF, RDFS, OWL, SHACL or SKOS
const BANNED_IRI_DOMAINS = [
    "http://www.w3.org/2004/02/skos",
    "http://www.w3.org/2008/05/skos-xl#",
    "http://www.w3.org/2002/07/owl",
    "http://www.w3.org/ns/shacl",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns",
    "http://www.w3.org/2000/01/rdf-schema#"
];

class IdentifierDialog extends React.Component {

    state = {
        open: false,
        isValidIRI: false,
        isDeleted: false,
        foldIRIs: this.props.showFolding,
        processingIRI: false,
        iri: this.props.currentIRI
    };

    handleClickOpen = () => {
        this.setState({ open: true, iri: this.props.currentIRI, isDeleted: false, isValidIRI: false, foldIRIs: true });
    };

    reserveIDs = async () => {
        if (!this.props.classIRI) return;
        this.setState({ processingIRI: true });
        // Make IRI reservation call
        const isFound = await GS.reserveIRI(this.state.iri, this.props.graph, this.props.classIRI);
        const exists = isFound.exists;
        if (exists) {
            this.rejectIRI(true);
            return false;
        }
        const reservationID = isFound.id;
        return {
            reservationID
        };
    };

    rejectIRI = () => this.setState({ isDuplicate: true, processingIRI: false });

    loadIDs() {
        this.cancellable = makeCancelable(this.reserveIDs());
        return this.cancellable;
    }

    changeIRI = async () => {
        try {
            const ids = await this.loadIDs().promise;
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ open: false });
                this.props.changeIRI(this.state.iri, this.state.foldIRIs);
                this.setState({
                    iri: "",
                    isValidIRI: false,
                    isDuplicate: false,
                    isDeleted: false,
                    processingIRI: false,
                    foldIRIs: true
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    exitChangeIRI = () => {
        this.setState({
            open: false,
            iri: "",
            processingIRI: false,
            isDuplicate: false
        });
    };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    findIRI = async (value) => {
        try {
            let data = {};
            if (GS.isDeleted(value, this.props.graph)) {
                data = {
                    exists: true,
                    deleted: true
                };
            }
            else {
                data = await this.doFind({
                    value,
                    includeDeleted: true,
                    graph: this.props.graph
                }).promise;
            }
            const isDuplicate = data && data.exists;
            const isDeleted = data && data.deleted;
            this.setState({
                isDuplicate,
                checked: true,
                isDeleted,
                isValidIRI: true,
                checking: false
            });
        }
        catch (e) {
            if (e.message === "The requested IRI cannot be processed.") {
                this.setState({
                    isDuplicate: false,
                    checking: false,
                    checked: true,
                    isValidIRI: false
                });
                return;
            }
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                this.setState({ isErrored: e, checking: false, isValidIRI: false });
            }
        }
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    copyBaseIRI = () => this.setState({ iri: this.props.baseIRI, isValidIRI: false });

    resetIRI = () => this.setState({ iri: this.props.currentIRI });

    handleIRIChange = event => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const text = sanitiseText(t.value.trim());
        this.setState({
            [t.name]: text,
            checking: true,
            isDuplicate: false,
            isDeleted: false,
            checked: false
        }, () => {
            if (text === this.props.currentIRI) {
                this.setState({ checking: false, checked: true });
                return;
            }
            const isBannedIRI = BANNED_IRI_DOMAINS.some(bd => text.toLowerCase().startsWith(bd));
            if (isBannedIRI || text.length === 0 || !detailedCheckIRI(text)) {
                this.setState({ isValidIRI: false, checking: false, checked: true });
            }
            else {
                debounce(this.findIRI, this, [text]);
            }
        });
    };

    clearError = async () => this.setState({ isErrored: false });

    handleChangeFolding = (event) => this.setState({ foldIRIs: event.target.checked });

    render() {

        const { classes, baseIRI, currentIRI, showFolding } = this.props;
        const { iri, isErrored, isDuplicate, checked, checking, isValidIRI, foldIRIs, isDeleted, processingIRI } = this.state;

        if (isErrored) return <Errored dataTest="id-error" data={isErrored} clearError={this.clearError} />;

        let icon = null;
        let additionalIcon = null;

        const containsRTL = iri && iri.match(RTL_REGEX);

        if (checking) {
            icon =
                <Tooltip title="Waiting for server to check IRI">
                    <InputAdornment position="end">
                        <span>...</span>
                    </InputAdornment>
                </Tooltip>;
        }
        else if (iri && iri.length > 0 && !isDuplicate && checked && isValidIRI) {
            icon =
                <InputAdornment size="small" position="end">
                    <Tooltip title="IRI valid" className={classes.smallIconButton}>
                        <IconButton aria-label="Confirm change" className={classes.smallIconButton}>
                            <DoneIcon />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }
        else if (isDuplicate || (!isValidIRI && iri && iri.length > 0 && iri !== this.props.currentIRI)) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title={
                        containsRTL ?
                            "Right-to-left text is not supported."
                            :
                            "The value is either invalid or duplicating an existing IRI"
                    }>
                        <WarningIcon data-test="warning" />
                    </Tooltip>
                </InputAdornment>;
        }
        if (iri && iri.length > 0 && iri !== this.props.currentIRI) {
            additionalIcon =
                <InputAdornment position="end" onClick={this.resetIRI}>
                    <Tooltip title="Reset to current IRI">
                        <IconButton aria-label="Reset to current IRI" className={classes.smallIconButton}>
                            <RefreshIcon data-test="refresh" />
                        </IconButton>
                    </Tooltip>
                </InputAdornment>;
        }

        return (
            <UserConsumer>
                {({ subscriptions, activeSubscription, invitations: userInvitations }) => {
                    const accessedSubscriptions = [...subscriptions, ...userInvitations];
                    const subscription = accessedSubscriptions && accessedSubscriptions.find(resource => {
                        if (resource.type === "Subscription" && resource.id === activeSubscription) return true;
                        if (resource.type === "Invitation" && resource.subscription === activeSubscription) return true;
                        return false;
                    });
                    const subscriptionVersion = "subscriptionVersion" in subscription ?
                        subscription.subscriptionVersion
                        :
                        0;
                    return <React.Fragment>
                        <InputAdornment position="end" onClick={this.handleClickOpen}>
                            <Tooltip title="Change IRI">
                                <IconButton
                                    aria-label="Change IRI"
                                    className={classes.smallIconButton}>
                                    <BuildIcon data-test="change-iri" fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                        <Dialog
                            data-test="change-iri-dialog"
                            maxWidth="md"
                            fullWidth
                            classes={{
                                paper: classes.paper,
                                paperWidthMd: classes.mediumDialog
                            }}
                            open={this.state.open}
                            onClose={this.exitChangeIRI}
                            aria-labelledby="iri-dialog-title">
                            <DialogTitle disableTypography className={classes.mainHeading} id="iri-dialog-title">Change IRI</DialogTitle>
                            <DialogContent style={{ height: '390px' }}>
                                {showFolding && <Help textRef="CHANGE_IRI" />}
                                <TitledPanel dataTest="currentIRI" title="Current IRI">
                                    <BasicReadField
                                        ariaLabel="Current IRI"
                                        disabled={true}
                                        dataTest="current-iri"
                                        margin="dense"
                                        text={currentIRI || "No IRI currently set."}
                                        fullWidth />
                                </TitledPanel>
                                <TitledPanel dataTest="baseIRI" title="Base IRI" margin={true}>
                                    <div style={{ display: 'flex' }}>
                                        <BasicReadField
                                            ariaLabel="Base IRI"
                                            disabled={true}
                                            dataTest="baseIRI"
                                            margin="dense"
                                            text={baseIRI || "No base IRI is available"}
                                            fullWidth />
                                        {baseIRI &&
                                            <div style={{ textAlign: 'right', width: 40, marginTop: 4 }}>
                                                <Tooltip title="Copy Base IRI">
                                                    <IconButton
                                                        aria-label="Copy Base IRI"
                                                        className={classes.smallIconButton}
                                                        style={{ backgroundColor: '#13294B', color: 'white' }}
                                                        onClick={this.copyBaseIRI}>
                                                        <ArrowDownwardIcon data-test="base-iri" fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        }
                                    </div>
                                </TitledPanel>
                                <div data-test="new-iri" style={{ paddingTop: 20 }}>
                                    <form autoComplete="off" noValidate="novalidate">
                                        <TitledTextField
                                            dataTest="iri"
                                            name="iri"
                                            title="New IRI"
                                            margin={true}
                                            required={true}
                                            text={iri || ""}
                                            placeholder="Enter an IRI starting with http or https"
                                            maxLength={MAX_LONGIRI_LENGTH}
                                            adornment={
                                                <div style={{ display: 'inline-flex' }}>
                                                    {additionalIcon}
                                                    {icon}
                                                </div>
                                            }
                                            helperText={
                                                isDeleted ?
                                                    "This is a deleted IRI and cannot be used. Please change."
                                                    :
                                                    isDuplicate ?
                                                        "This IRI already exists. Please change."
                                                        :
                                                        ""}
                                            onChange={this.handleIRIChange} />
                                    </form>
                                </div>
                                {showFolding && subscriptionVersion < 3 &&
                                    <div style={{ flex: 1 }}>
                                        <FormControlLabel
                                            data-test="option-label"
                                            control={
                                                <Checkbox data-test="make-permanent" checked={foldIRIs} onChange={this.handleChangeFolding} name="folding" />}
                                            label="Make All Changes Permanent"
                                            classes={{ label: classes.formLabelInline }}
                                        />
                                    </div>
                                }
                            </DialogContent>
                            <DialogActions className={classes.dialogActions}>
                                <CancelButton onClick={this.exitChangeIRI} />
                                <PrimaryButton
                                    disabled={processingIRI || !isValidIRI || isDuplicate || checking || iri === this.props.currentIRI}
                                    onClick={this.changeIRI}>Change</PrimaryButton>
                            </DialogActions>
                        </Dialog>
                    </React.Fragment>;
                }}
            </UserConsumer>
        );
    }
}

IdentifierDialog.defaultProps = {
    showFolding: true
};

IdentifierDialog.propTypes = {
    changeIRI: PropTypes.func.isRequired,
    currentIRI: PropTypes.string,
    baseIRI: PropTypes.string,
    classIRI: PropTypes.string,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(IdentifierDialog);